import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink, NavigationEnd } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { ExamInstanceEvaluationStatusEnumLabels } from 'src/app/enums/exam-instance-evaluation-status.enum';
import { ExamInstanceEvaluation } from 'src/app/models/exam_instance_evaluation.model';
import { MeetingRecord } from 'src/app/models/meeting-record.interface';
import { JobVacancyCandidatePhaseProgress } from 'src/app/models/job_candidate.model';
import { ApiService, UtilsService } from 'src/app/services';

interface RecruitmentMeetingRecordSerialized extends MeetingRecord {
  content_excerpt: string;
  phase_name: string;
}

@Component({
  selector: 'app-job-vacancy-candidate-meeting-records-list',
  templateUrl: './job-vacancy-candidate-meeting-records-list.component.html',
  styleUrl: './job-vacancy-candidate-meeting-records-list.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe, RouterLink, MatButtonModule, MatIconModule, MatTooltipModule],
})
export class JobCandidateMeetingRecordsListComponent implements OnInit {
  private readonly api_service = inject(ApiService);
  private readonly utils_service = inject(UtilsService);
  private readonly router = inject(Router);
  private readonly snack_bar = inject(MatSnackBar);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly change_detector_ref = inject(ChangeDetectorRef);

  readonly job_vacancy_id = input.required<number>();
  readonly job_candidate_id = input.required<number>();
  readonly is_discarded = input.required<boolean>();

  readonly loading = signal(true);
  readonly meeting_records = signal<RecruitmentMeetingRecordSerialized[]>([]);
  readonly evaluations_by_id = signal<Record<number, ExamInstanceEvaluation>>({});
  readonly candidate_user_id = signal<number | null>(null);

  ngOnInit(): void {
    this.loadMeetingRecords();
    this.listenCandidateDetailNavigation();
  }

  reloadMeetingRecords(): void {
    this.loadMeetingRecords();
  }

  deleteMeetingRecord(meeting_record: RecruitmentMeetingRecordSerialized): void {
    if (this.is_discarded() || !confirm('¿Eliminar este acta?')) {
      return;
    }

    this.api_service
      .deleteMeetingRecord(String(meeting_record.id))
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (): void => {
          this.loadMeetingRecords();
          this.snack_bar.open('Acta eliminada', undefined, { duration: 3000 });
        },
        error: (err: { error?: { error?: string } }): void => {
          const message = err?.error?.error ?? 'Error al eliminar el acta.';
          this.snack_bar.open(message, undefined, { duration: 5000 });
        },
      });
  }

  private listenCandidateDetailNavigation(): void {
    const candidate_detail_path = `/employees/rrhh/job-vacancies/${this.job_vacancy_id()}/candidates/${this.job_candidate_id()}`;

    this.router.events
      .pipe(
        filter((navigation_event): navigation_event is NavigationEnd => navigation_event instanceof NavigationEnd),
        filter(() => {
          const current_url = this.router.url.split('?')[0];

          return current_url === candidate_detail_path;
        }),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe(() => {
        this.loadMeetingRecords();
      });
  }

  private loadMeetingRecords(): void {
    this.loading.set(true);

    this.api_service
      .getJobCandidate(this.job_candidate_id())
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: job_candidate => {
          const user_id = job_candidate.user_id;
          this.candidate_user_id.set(user_id);

          forkJoin({
            meeting_records: this.api_service.getMeetingRecords({
              scopes: `jobVacancyCandidate:${this.job_candidate_id()}`,
              per_page: 100,
              order_by: 'created_at:desc',
              with: 'meetingable.job_vacancy_phase,exam_instance_evaluation.exam_instance',
            }),
            exam_instance_evaluations: user_id
              ? this.api_service.getExamInstanceEvaluations({
                  user_id,
                  per_page: 500,
                  order_by: 'created_at:desc',
                  with: 'exam_instance',
                })
              : of([] as ExamInstanceEvaluation[]),
          })
            .pipe(takeUntilDestroyed(this.destroy_ref))
            .subscribe({
              next: ({ meeting_records, exam_instance_evaluations }): void => {
                const normalized_meeting_records = Array.isArray(meeting_records) ? meeting_records : meeting_records.data;
                this.meeting_records.set(normalized_meeting_records.map(meeting_record => this.serializeMeetingRecord(meeting_record)));
                this.evaluations_by_id.set(this.buildEvaluationsById(exam_instance_evaluations));
                this.loading.set(false);
                this.change_detector_ref.markForCheck();
              },
              error: (): void => {
                this.loading.set(false);
                this.snack_bar.open('No se pudieron cargar las actas.', undefined, { duration: 5000 });
                this.change_detector_ref.markForCheck();
              },
            });
        },
        error: (): void => {
          this.loading.set(false);
          this.snack_bar.open('No se pudieron cargar las actas.', undefined, { duration: 5000 });
          this.change_detector_ref.markForCheck();
        },
      });
  }

  protected resolveEvaluationForMeetingRecord(meeting_record: RecruitmentMeetingRecordSerialized): ExamInstanceEvaluation | null {
    const exam_instance_evaluation_id = meeting_record.exam_instance_evaluation_id;

    if (exam_instance_evaluation_id == null) {
      return null;
    }

    return this.evaluations_by_id()[exam_instance_evaluation_id] ?? null;
  }

  protected evaluationDetailRouterLink(meeting_record: RecruitmentMeetingRecordSerialized): (string | number)[] | null {
    const evaluation = this.resolveEvaluationForMeetingRecord(meeting_record);

    if (evaluation === null || evaluation.exam_instance_id === null) {
      return null;
    }

    return ['/employees', 'rrhh', 'exam-instances', evaluation.exam_instance_id, 'evaluations', evaluation.id];
  }

  protected evaluationStatusLabel(meeting_record: RecruitmentMeetingRecordSerialized): string {
    const evaluation = this.resolveEvaluationForMeetingRecord(meeting_record);

    if (evaluation === null) {
      return 'Sin evaluación';
    }

    return ExamInstanceEvaluationStatusEnumLabels[evaluation.status] ?? evaluation.status;
  }

  private serializeMeetingRecord(meeting_record: MeetingRecord): RecruitmentMeetingRecordSerialized {
    return {
      ...meeting_record,
      content_excerpt: this.utils_service.buildHtmlContentExcerpt(meeting_record.content ?? meeting_record.ai_summary_html ?? null),
      phase_name: this.resolveMeetingRecordPhaseName(meeting_record),
    };
  }

  private resolveMeetingRecordPhaseName(meeting_record: MeetingRecord): string {
    if (meeting_record.meetingable_type !== 'App\\Models\\JobVacancyCandidatePhaseProgress') {
      return 'Sin fase';
    }

    const phase_progress = meeting_record.meetingable as JobVacancyCandidatePhaseProgress | undefined;

    return phase_progress?.job_vacancy_phase?.name ?? 'Sin fase';
  }

  private buildEvaluationsById(
    response: ArrayResponse<ExamInstanceEvaluation> | ExamInstanceEvaluation[]
  ): Record<number, ExamInstanceEvaluation> {
    const evaluations = Array.isArray(response) ? response : response.data;
    const evaluations_by_id: Record<number, ExamInstanceEvaluation> = {};

    for (const exam_instance_evaluation of evaluations) {
      evaluations_by_id[exam_instance_evaluation.id] = exam_instance_evaluation;
    }

    return evaluations_by_id;
  }
}
